/**
 * ONTOLOGY - Taxonomía de Conceptos para Appmor
 * 
 * Define la jerarquía de conceptos de salud mental y bienestar.
 * Permite:
 * - Normalizar términos (ansiedad = nerviosismo = angustia)
 * - Expandir búsquedas (mindfulness → respiración, meditación, presencia)
 * - Categorizar contenido automáticamente
 * 
 * ESTRUCTURA:
 * - Nivel 0: Raíz (bienestar_emocional)
 * - Nivel 1: Categorías principales (regulación, relaciones, presencia...)
 * - Nivel 2: Subcategorías (ansiedad, estrés, meditación...)
 * - Nivel 3: Técnicas específicas (respiración 4-7-8, body scan...)
 */

// ============================================
// TIPOS
// ============================================

export interface Concept {
  id: string;
  label: string;
  labelEs: string;           // Label en español
  parent?: string;           // ID del concepto padre
  children?: string[];       // IDs de conceptos hijos
  synonyms: string[];        // Sinónimos y términos relacionados
  relatedConcepts?: string[]; // Conceptos relacionados (no jerárquicos)
  level: 0 | 1 | 2 | 3;      // Nivel en la jerarquía
  
  // Metadata adicional
  description?: string;
  scientificEvidence?: 'alta' | 'moderada' | 'baja' | 'emergente';
  amarCategory?: 'presence' | 'compasivos' | 'admirativos' | 'fraternales' | 'eroticos';
}

export type ConceptId = string;

// ============================================
// ONTOLOGÍA DE CONCEPTOS
// ============================================

export const CONCEPT_ONTOLOGY: Record<ConceptId, Concept> = {
  
  // ==========================================
  // NIVEL 0: RAÍZ
  // ==========================================
  
  "bienestar_emocional": {
    id: "bienestar_emocional",
    label: "Emotional Wellbeing",
    labelEs: "Bienestar Emocional",
    level: 0,
    children: [
      "regulacion_emocional",
      "presencia_mindfulness",
      "relaciones",
      "autoconocimiento",
      "proposito_sentido"
    ],
    synonyms: [
      "salud mental", "salud emocional", "equilibrio emocional",
      "bienestar psicológico", "wellness", "wellbeing"
    ]
  },
  
  // ==========================================
  // NIVEL 1: CATEGORÍAS PRINCIPALES
  // ==========================================
  
  "regulacion_emocional": {
    id: "regulacion_emocional",
    label: "Emotional Regulation",
    labelEs: "Regulación Emocional",
    parent: "bienestar_emocional",
    level: 1,
    children: [
      "manejo_ansiedad",
      "manejo_estres",
      "manejo_ira",
      "manejo_tristeza",
      "tolerancia_frustracion"
    ],
    synonyms: [
      "gestión emocional", "control emocional", "equilibrio emocional",
      "autorregulación", "manejo de emociones", "inteligencia emocional"
    ],
    amarCategory: "compasivos"
  },
  
  "presencia_mindfulness": {
    id: "presencia_mindfulness",
    label: "Presence / Mindfulness",
    labelEs: "Presencia / Mindfulness",
    parent: "bienestar_emocional",
    level: 1,
    children: [
      "meditacion",
      "respiracion_consciente",
      "grounding",
      "atencion_plena",
      "observacion_pensamientos"
    ],
    synonyms: [
      "mindfulness", "atención plena", "consciencia plena", "presente",
      "aquí y ahora", "momento presente", "awareness", "conciencia"
    ],
    amarCategory: "presence"
  },
  
  "relaciones": {
    id: "relaciones",
    label: "Relationships",
    labelEs: "Relaciones",
    parent: "bienestar_emocional",
    level: 1,
    children: [
      "comunicacion",
      "limites_saludables",
      "apego",
      "conflictos",
      "intimidad"
    ],
    synonyms: [
      "vínculos", "conexiones", "relaciones interpersonales",
      "relaciones afectivas", "pareja", "familia", "amistades"
    ],
    amarCategory: "fraternales"
  },
  
  "autoconocimiento": {
    id: "autoconocimiento",
    label: "Self-Knowledge",
    labelEs: "Autoconocimiento",
    parent: "bienestar_emocional",
    level: 1,
    children: [
      "autoestima",
      "identidad",
      "valores_personales",
      "fortalezas",
      "sombra"
    ],
    synonyms: [
      "conocerse a uno mismo", "introspección", "reflexión personal",
      "autodescubrimiento", "self-awareness", "consciencia de sí"
    ],
    amarCategory: "admirativos"
  },
  
  "proposito_sentido": {
    id: "proposito_sentido",
    label: "Purpose & Meaning",
    labelEs: "Propósito y Sentido",
    parent: "bienestar_emocional",
    level: 1,
    children: [
      "metas_vitales",
      "motivacion",
      "resiliencia",
      "crecimiento_personal",
      "espiritualidad"
    ],
    synonyms: [
      "sentido de vida", "propósito vital", "significado",
      "para qué", "misión", "vocación", "ikigai"
    ],
    amarCategory: "admirativos"
  },
  
  // ==========================================
  // NIVEL 2: SUBCATEGORÍAS
  // ==========================================
  
  // --- Regulación Emocional ---
  
  "manejo_ansiedad": {
    id: "manejo_ansiedad",
    label: "Anxiety Management",
    labelEs: "Manejo de Ansiedad",
    parent: "regulacion_emocional",
    level: 2,
    children: [
      "respiracion_4_7_8",
      "relajacion_progresiva",
      "tecnica_stop",
      "exposicion_gradual"
    ],
    synonyms: [
      "ansiedad", "nerviosismo", "nervios", "angustia", "preocupación",
      "inquietud", "intranquilidad", "anxiety", "worried", "anxious",
      "miedo", "temor", "pánico", "fobia"
    ],
    relatedConcepts: ["respiracion_consciente", "grounding", "meditacion"],
    scientificEvidence: "alta"
  },
  
  "manejo_estres": {
    id: "manejo_estres",
    label: "Stress Management",
    labelEs: "Manejo del Estrés",
    parent: "regulacion_emocional",
    level: 2,
    children: [
      "relajacion_progresiva",
      "respiracion_diafragmatica",
      "descanso_activo"
    ],
    synonyms: [
      "estrés", "estres", "agotamiento", "burnout", "sobrecarga",
      "tensión", "presión", "stress", "overwhelm", "agobio",
      "saturación", "cansancio mental"
    ],
    relatedConcepts: ["manejo_ansiedad", "descanso", "limites_saludables"],
    scientificEvidence: "alta"
  },
  
  "manejo_ira": {
    id: "manejo_ira",
    label: "Anger Management",
    labelEs: "Manejo de la Ira",
    parent: "regulacion_emocional",
    level: 2,
    synonyms: [
      "ira", "rabia", "enfado", "enojo", "frustración",
      "irritabilidad", "anger", "rage", "coraje", "cólera"
    ],
    relatedConcepts: ["comunicacion", "respiracion_consciente"],
    scientificEvidence: "alta"
  },
  
  "manejo_tristeza": {
    id: "manejo_tristeza",
    label: "Sadness Management",
    labelEs: "Manejo de la Tristeza",
    parent: "regulacion_emocional",
    level: 2,
    synonyms: [
      "tristeza", "melancolía", "pena", "duelo", "pérdida",
      "depresión", "desánimo", "apatía", "sadness", "grief",
      "desolación", "vacío"
    ],
    relatedConcepts: ["autocompasion", "proposito_sentido"],
    scientificEvidence: "alta"
  },
  
  "tolerancia_frustracion": {
    id: "tolerancia_frustracion",
    label: "Frustration Tolerance",
    labelEs: "Tolerancia a la Frustración",
    parent: "regulacion_emocional",
    level: 2,
    synonyms: [
      "frustración", "impaciencia", "desesperación",
      "no aguanto", "quiero ya", "frustration"
    ],
    relatedConcepts: ["manejo_ira", "resiliencia"]
  },
  
  // --- Presencia / Mindfulness ---
  
  "meditacion": {
    id: "meditacion",
    label: "Meditation",
    labelEs: "Meditación",
    parent: "presencia_mindfulness",
    level: 2,
    children: [
      "meditacion_guiada",
      "meditacion_silencio",
      "body_scan",
      "loving_kindness"
    ],
    synonyms: [
      "meditar", "meditation", "contemplación", "práctica meditativa",
      "sentarse en silencio", "quietud", "recogimiento"
    ],
    scientificEvidence: "alta"
  },
  
  "respiracion_consciente": {
    id: "respiracion_consciente",
    label: "Conscious Breathing",
    labelEs: "Respiración Consciente",
    parent: "presencia_mindfulness",
    level: 2,
    children: [
      "respiracion_4_7_8",
      "respiracion_diafragmatica",
      "coherencia_cardiaca",
      "respiracion_cuadrada"
    ],
    synonyms: [
      "respiración", "respirar", "breathing", "técnicas respiratorias",
      "ejercicios de respiración", "breath work", "pranayama"
    ],
    relatedConcepts: ["manejo_ansiedad", "manejo_estres", "meditacion"],
    scientificEvidence: "alta"
  },
  
  "grounding": {
    id: "grounding",
    label: "Grounding",
    labelEs: "Grounding / Anclaje",
    parent: "presencia_mindfulness",
    level: 2,
    children: [
      "grounding_5_4_3_2_1",
      "enraizamiento",
      "contacto_tierra"
    ],
    synonyms: [
      "anclaje", "arraigo", "enraizamiento", "puesta a tierra",
      "conectar con el cuerpo", "volver al presente", "anclar"
    ],
    relatedConcepts: ["manejo_ansiedad", "disociacion"],
    scientificEvidence: "moderada"
  },
  
  "atencion_plena": {
    id: "atencion_plena",
    label: "Mindful Attention",
    labelEs: "Atención Plena",
    parent: "presencia_mindfulness",
    level: 2,
    synonyms: [
      "atención", "foco", "concentración", "estar presente",
      "awareness", "consciencia", "darse cuenta"
    ]
  },
  
  "observacion_pensamientos": {
    id: "observacion_pensamientos",
    label: "Thought Observation",
    labelEs: "Observación de Pensamientos",
    parent: "presencia_mindfulness",
    level: 2,
    synonyms: [
      "observar pensamientos", "defusión cognitiva", "desidentificación",
      "no soy mis pensamientos", "testigo", "metacognición"
    ],
    relatedConcepts: ["meditacion", "reestructuracion_cognitiva"]
  },
  
  // --- Relaciones ---
  
  "comunicacion": {
    id: "comunicacion",
    label: "Communication",
    labelEs: "Comunicación",
    parent: "relaciones",
    level: 2,
    synonyms: [
      "comunicar", "expresar", "hablar", "decir lo que siento",
      "asertividad", "diálogo", "conversación", "escucha"
    ],
    relatedConcepts: ["limites_saludables", "conflictos"]
  },
  
  "limites_saludables": {
    id: "limites_saludables",
    label: "Healthy Boundaries",
    labelEs: "Límites Saludables",
    parent: "relaciones",
    level: 2,
    synonyms: [
      "límites", "boundaries", "decir no", "poner límites",
      "espacio personal", "respeto", "autocuidado relacional"
    ],
    relatedConcepts: ["autoestima", "comunicacion"]
  },
  
  "apego": {
    id: "apego",
    label: "Attachment",
    labelEs: "Apego",
    parent: "relaciones",
    level: 2,
    synonyms: [
      "attachment", "vínculo", "dependencia emocional",
      "apego seguro", "apego ansioso", "apego evitativo"
    ]
  },
  
  "conflictos": {
    id: "conflictos",
    label: "Conflict Resolution",
    labelEs: "Resolución de Conflictos",
    parent: "relaciones",
    level: 2,
    synonyms: [
      "conflicto", "pelea", "discusión", "problema de pareja",
      "reconciliación", "perdón", "resolver diferencias"
    ],
    relatedConcepts: ["comunicacion", "manejo_ira"]
  },
  
  "intimidad": {
    id: "intimidad",
    label: "Intimacy",
    labelEs: "Intimidad",
    parent: "relaciones",
    level: 2,
    synonyms: [
      "cercanía", "conexión profunda", "vulnerabilidad",
      "confianza", "apertura emocional"
    ],
    amarCategory: "eroticos"
  },
  
  // --- Autoconocimiento ---
  
  "autoestima": {
    id: "autoestima",
    label: "Self-Esteem",
    labelEs: "Autoestima",
    parent: "autoconocimiento",
    level: 2,
    synonyms: [
      "valorarse", "quererse", "amor propio", "self-esteem",
      "autovaloración", "confianza en uno mismo", "autoconcepto"
    ],
    relatedConcepts: ["autocompasion", "identidad"]
  },
  
  "identidad": {
    id: "identidad",
    label: "Identity",
    labelEs: "Identidad",
    parent: "autoconocimiento",
    level: 2,
    synonyms: [
      "quién soy", "yo", "self", "personalidad",
      "sentido de identidad", "autoimagen"
    ]
  },
  
  "valores_personales": {
    id: "valores_personales",
    label: "Personal Values",
    labelEs: "Valores Personales",
    parent: "autoconocimiento",
    level: 2,
    synonyms: [
      "valores", "principios", "qué es importante para mí",
      "brújula interna", "ethics", "moral personal"
    ],
    relatedConcepts: ["proposito_sentido", "metas_vitales"]
  },
  
  "fortalezas": {
    id: "fortalezas",
    label: "Strengths",
    labelEs: "Fortalezas",
    parent: "autoconocimiento",
    level: 2,
    synonyms: [
      "puntos fuertes", "talentos", "virtudes", "capacidades",
      "strengths", "cualidades", "dones"
    ],
    relatedConcepts: ["autoestima", "proposito_sentido"]
  },
  
  "sombra": {
    id: "sombra",
    label: "Shadow Work",
    labelEs: "Trabajo con la Sombra",
    parent: "autoconocimiento",
    level: 2,
    synonyms: [
      "shadow", "partes rechazadas", "inconsciente",
      "integración", "aceptar lo negativo"
    ]
  },
  
  // --- Propósito y Sentido ---
  
  "metas_vitales": {
    id: "metas_vitales",
    label: "Life Goals",
    labelEs: "Metas Vitales",
    parent: "proposito_sentido",
    level: 2,
    synonyms: [
      "objetivos", "sueños", "aspiraciones", "visión de vida",
      "goals", "planes de vida"
    ]
  },
  
  "motivacion": {
    id: "motivacion",
    label: "Motivation",
    labelEs: "Motivación",
    parent: "proposito_sentido",
    level: 2,
    synonyms: [
      "ganas", "impulso", "drive", "motor",
      "energía", "entusiasmo", "pasión"
    ]
  },
  
  "resiliencia": {
    id: "resiliencia",
    label: "Resilience",
    labelEs: "Resiliencia",
    parent: "proposito_sentido",
    level: 2,
    synonyms: [
      "fortaleza", "superar adversidad", "recuperarse",
      "adaptación", "aguante", "resistencia"
    ],
    scientificEvidence: "alta"
  },
  
  "crecimiento_personal": {
    id: "crecimiento_personal",
    label: "Personal Growth",
    labelEs: "Crecimiento Personal",
    parent: "proposito_sentido",
    level: 2,
    synonyms: [
      "desarrollo personal", "evolución", "mejora continua",
      "self-improvement", "superación", "transformación"
    ]
  },
  
  "espiritualidad": {
    id: "espiritualidad",
    label: "Spirituality",
    labelEs: "Espiritualidad",
    parent: "proposito_sentido",
    level: 2,
    synonyms: [
      "trascendencia", "conexión con algo mayor", "fe",
      "sentido profundo", "alma", "espíritu"
    ]
  },
  
  // ==========================================
  // NIVEL 3: TÉCNICAS ESPECÍFICAS
  // ==========================================
  
  // --- Técnicas de Respiración ---
  
  "respiracion_4_7_8": {
    id: "respiracion_4_7_8",
    label: "4-7-8 Breathing",
    labelEs: "Respiración 4-7-8",
    parent: "respiracion_consciente",
    level: 3,
    synonyms: [
      "4-7-8", "cuatro siete ocho", "respiración relajante",
      "técnica del Dr. Weil", "breath 4 7 8"
    ],
    description: "Inhalar 4 segundos, retener 7, exhalar 8",
    scientificEvidence: "moderada"
  },
  
  "respiracion_diafragmatica": {
    id: "respiracion_diafragmatica",
    label: "Diaphragmatic Breathing",
    labelEs: "Respiración Diafragmática",
    parent: "respiracion_consciente",
    level: 3,
    synonyms: [
      "respiración abdominal", "respiración de barriga",
      "respiración profunda", "belly breathing"
    ],
    scientificEvidence: "alta"
  },
  
  "coherencia_cardiaca": {
    id: "coherencia_cardiaca",
    label: "Cardiac Coherence",
    labelEs: "Coherencia Cardíaca",
    parent: "respiracion_consciente",
    level: 3,
    synonyms: [
      "heart coherence", "variabilidad cardíaca",
      "respiración 5-5", "HRV breathing"
    ],
    scientificEvidence: "alta"
  },
  
  "respiracion_cuadrada": {
    id: "respiracion_cuadrada",
    label: "Box Breathing",
    labelEs: "Respiración Cuadrada",
    parent: "respiracion_consciente",
    level: 3,
    synonyms: [
      "box breathing", "respiración 4-4-4-4",
      "respiración táctica", "navy seal breathing"
    ],
    scientificEvidence: "moderada"
  },
  
  // --- Técnicas de Meditación ---
  
  "body_scan": {
    id: "body_scan",
    label: "Body Scan",
    labelEs: "Escaneo Corporal",
    parent: "meditacion",
    level: 3,
    synonyms: [
      "escáner corporal", "recorrido por el cuerpo",
      "exploración corporal", "body awareness"
    ],
    relatedConcepts: ["relajacion_progresiva", "grounding"],
    scientificEvidence: "alta"
  },
  
  "meditacion_guiada": {
    id: "meditacion_guiada",
    label: "Guided Meditation",
    labelEs: "Meditación Guiada",
    parent: "meditacion",
    level: 3,
    synonyms: [
      "visualización guiada", "meditación con audio",
      "guided imagery"
    ]
  },
  
  "meditacion_silencio": {
    id: "meditacion_silencio",
    label: "Silent Meditation",
    labelEs: "Meditación en Silencio",
    parent: "meditacion",
    level: 3,
    synonyms: [
      "zazen", "vipassana", "meditación sin guía",
      "sentarse en silencio"
    ]
  },
  
  "loving_kindness": {
    id: "loving_kindness",
    label: "Loving Kindness",
    labelEs: "Meditación de Amor y Bondad",
    parent: "meditacion",
    level: 3,
    synonyms: [
      "metta", "compasión", "bondad amorosa",
      "enviar amor", "autocompasión meditativa"
    ],
    relatedConcepts: ["autocompasion"],
    scientificEvidence: "alta"
  },
  
  // --- Técnicas de Grounding ---
  
  "grounding_5_4_3_2_1": {
    id: "grounding_5_4_3_2_1",
    label: "5-4-3-2-1 Grounding",
    labelEs: "Técnica 5-4-3-2-1",
    parent: "grounding",
    level: 3,
    synonyms: [
      "5 4 3 2 1", "cinco sentidos", "grounding sensorial",
      "técnica de los sentidos"
    ],
    description: "5 cosas que ves, 4 que oyes, 3 que tocas, 2 que hueles, 1 que saboreas",
    scientificEvidence: "moderada"
  },
  
  "enraizamiento": {
    id: "enraizamiento",
    label: "Rooting",
    labelEs: "Enraizamiento",
    parent: "grounding",
    level: 3,
    synonyms: [
      "echar raíces", "sentir los pies", "contacto con suelo",
      "tree grounding"
    ]
  },
  
  // --- Técnicas para Ansiedad ---
  
  "relajacion_progresiva": {
    id: "relajacion_progresiva",
    label: "Progressive Relaxation",
    labelEs: "Relajación Muscular Progresiva",
    parent: "manejo_ansiedad",
    level: 3,
    synonyms: [
      "Jacobson", "tensión-relajación", "PMR",
      "relajación muscular", "progressive muscle relaxation"
    ],
    scientificEvidence: "alta"
  },
  
  "tecnica_stop": {
    id: "tecnica_stop",
    label: "STOP Technique",
    labelEs: "Técnica STOP",
    parent: "manejo_ansiedad",
    level: 3,
    synonyms: [
      "S.T.O.P.", "parar observar proceder",
      "stop technique", "mindful STOP"
    ],
    description: "Stop, Take a breath, Observe, Proceed"
  },
  
  "exposicion_gradual": {
    id: "exposicion_gradual",
    label: "Gradual Exposure",
    labelEs: "Exposición Gradual",
    parent: "manejo_ansiedad",
    level: 3,
    synonyms: [
      "desensibilización", "exposición", "enfrentar miedos",
      "exposure therapy"
    ],
    scientificEvidence: "alta"
  },
  
  // --- Conceptos adicionales frecuentes ---
  
  "autocompasion": {
    id: "autocompasion",
    label: "Self-Compassion",
    labelEs: "Autocompasión",
    parent: "autoconocimiento",
    level: 2,
    synonyms: [
      "compasión hacia uno mismo", "self-compassion",
      "tratarse con cariño", "ser amable conmigo"
    ],
    relatedConcepts: ["autoestima", "loving_kindness"],
    scientificEvidence: "alta"
  },
  
  "sueno_descanso": {
    id: "sueno_descanso",
    label: "Sleep & Rest",
    labelEs: "Sueño y Descanso",
    parent: "regulacion_emocional",
    level: 2,
    synonyms: [
      "dormir", "insomnio", "descanso", "sueño",
      "problemas de sueño", "no puedo dormir", "sleep"
    ],
    relatedConcepts: ["respiracion_4_7_8", "body_scan", "relajacion_progresiva"]
  },
  
  "trauma": {
    id: "trauma",
    label: "Trauma",
    labelEs: "Trauma",
    parent: "regulacion_emocional",
    level: 2,
    synonyms: [
      "traumático", "TEPT", "PTSD", "experiencia traumática",
      "herida emocional", "evento traumático"
    ],
    relatedConcepts: ["grounding", "resiliencia"],
    scientificEvidence: "alta"
  },
  
  "duelo": {
    id: "duelo",
    label: "Grief",
    labelEs: "Duelo",
    parent: "manejo_tristeza",
    level: 3,
    synonyms: [
      "pérdida", "luto", "grief", "mourning",
      "despedida", "muerte de un ser querido"
    ]
  }
};

// ============================================
// FUNCIONES DE UTILIDAD
// ============================================

/**
 * Obtiene un concepto por su ID
 */
export function getConcept(conceptId: ConceptId): Concept | undefined {
  return CONCEPT_ONTOLOGY[conceptId];
}

/**
 * Obtiene todos los conceptos de un nivel específico
 */
export function getConceptsByLevel(level: 0 | 1 | 2 | 3): Concept[] {
  return Object.values(CONCEPT_ONTOLOGY).filter(c => c.level === level);
}

/**
 * Obtiene los hijos directos de un concepto
 */
export function getChildren(conceptId: ConceptId): Concept[] {
  const concept = CONCEPT_ONTOLOGY[conceptId];
  if (!concept?.children) return [];
  return concept.children.map(id => CONCEPT_ONTOLOGY[id]).filter(Boolean);
}

/**
 * Obtiene todos los descendientes de un concepto (recursivo)
 */
export function getAllDescendants(conceptId: ConceptId): Concept[] {
  const children = getChildren(conceptId);
  const descendants: Concept[] = [...children];
  
  for (const child of children) {
    descendants.push(...getAllDescendants(child.id));
  }
  
  return descendants;
}

/**
 * Obtiene el path completo desde la raíz hasta un concepto
 */
export function getConceptPath(conceptId: ConceptId): Concept[] {
  const path: Concept[] = [];
  let current = CONCEPT_ONTOLOGY[conceptId];
  
  while (current) {
    path.unshift(current);
    current = current.parent ? CONCEPT_ONTOLOGY[current.parent] : undefined;
  }
  
  return path;
}

/**
 * Obtiene conceptos relacionados (no jerárquicos)
 */
export function getRelatedConcepts(conceptId: ConceptId): Concept[] {
  const concept = CONCEPT_ONTOLOGY[conceptId];
  if (!concept?.relatedConcepts) return [];
  return concept.relatedConcepts.map(id => CONCEPT_ONTOLOGY[id]).filter(Boolean);
}

/**
 * Obtiene todos los sinónimos de un concepto
 */
export function getAllSynonyms(conceptId: ConceptId): string[] {
  const concept = CONCEPT_ONTOLOGY[conceptId];
  if (!concept) return [];
  return [concept.label, concept.labelEs, ...concept.synonyms];
}





